import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { invalidateLeaderboard } from '../utils/redisCache';

const BACKOFF_BASE = 1000; // 1s
const BACKOFF_MAX = 30000; // 30s
const MAX_RETRIES = 8;
const MAX_ACTIVITIES = 20;

/**
 * useDashboardRealtime
 * Subscribes to:
 *   - player_stats (energy, XP, cash, level)
 *   - net_worth_snapshots (live net worth + 7-day trend)
 *   - portfolio_holdings (live portfolio value)
 *   - factions (vault balance changes)
 *   - faction_members (rank/faction changes)
 *   - audit_logs (activity feed — trades, crimes, businesses, faction actions)
 */
export function useDashboardRealtime({
  userId,
  initialPlayerStats,
  initialHoldings,
  initialFaction,
  onLevelUp,
  onVaultUpdate,
}) {
  const [realtimePlayerStats, setRealtimePlayerStats] = useState(null);
  const [realtimeHoldings, setRealtimeHoldings] = useState(null);
  const [realtimeFaction, setRealtimeFaction] = useState(null);
  const [realtimeActivities, setRealtimeActivities] = useState(null);
  const [netWorthTrend, setNetWorthTrend] = useState([]);
  const [netWorthChange, setNetWorthChange] = useState({ change: 0, changePercent: 0 });
  const [connectionStatus, setConnectionStatus] = useState('connecting'); // 'connected' | 'reconnecting' | 'offline'
  const [lastUpdated, setLastUpdated] = useState(null);
  const [levelUpNotif, setLevelUpNotif] = useState(null);
  const [vaultToast, setVaultToast] = useState(null);
  const [subscriptionHealth, setSubscriptionHealth] = useState({});

  const channelsRef = useRef([]);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const prevLevelRef = useRef(null);
  const prevVaultRef = useRef(null);
  const mountedRef = useRef(true);
  const connectedCountRef = useRef(0);
  const totalChannelsRef = useRef(0);

  const markUpdated = useCallback(() => {
    setLastUpdated(new Date());
  }, []);

  const updateHealth = useCallback((channelName, status) => {
    setSubscriptionHealth(prev => ({ ...prev, [channelName]: status }));
  }, []);

  // Dismiss vault toast after 5s
  const showVaultToast = useCallback((message) => {
    setVaultToast(message);
    setTimeout(() => {
      if (mountedRef?.current) setVaultToast(null);
    }, 5000);
  }, []);

  // Fetch fresh portfolio holdings
  const refreshHoldings = useCallback(async () => {
    if (!userId) return;
    const { data, error } = await supabase
      ?.from('portfolio_holdings')
      ?.select('quantity, avg_buy_price, stocks(ticker, company_name, current_price, sector, open_price)')
      ?.eq('user_id', userId)
      ?.gt('quantity', 0);
    if (!error && data && mountedRef?.current) {
      const mapped = data?.map(h => ({
        ticker: h?.stocks?.ticker || 'N/A',
        name: h?.stocks?.company_name || 'Unknown',
        shares: parseInt(h?.quantity),
        value: parseInt(h?.quantity) * parseFloat(h?.stocks?.current_price || 0),
        change: h?.stocks?.open_price > 0
          ? parseFloat(((h?.stocks?.current_price - h?.stocks?.open_price) / h?.stocks?.open_price * 100)?.toFixed(2))
          : 0,
      }));
      setRealtimeHoldings(mapped);
      markUpdated();
      // Invalidate leaderboard cache when portfolio changes (net worth may have changed)
      try { invalidateLeaderboard(); } catch {}
    }
  }, [userId, markUpdated]);

  // Fetch fresh faction info
  const refreshFaction = useCallback(async (payload) => {
    if (!userId) return;
    const { data, error } = await supabase
      ?.from('faction_members')
      ?.select('rank, factions(id, faction_name, tag, banner_color, total_vault_balance, faction_xp, territory_count)')
      ?.eq('user_id', userId)
      ?.maybeSingle();
    if (!error && data?.factions && mountedRef?.current) {
      const f = data?.factions;
      const newVault = parseFloat(f?.total_vault_balance || 0);

      // Vault change notification
      if (prevVaultRef?.current !== null && prevVaultRef?.current !== newVault) {
        const diff = newVault - prevVaultRef?.current;
        const sign = diff >= 0 ? '+' : '-';
        const actor = payload?.new?.updated_by_username || payload?.new?.last_action_by || 'Member';
        showVaultToast(`Vault updated: ${sign}$${Math.abs(diff)?.toLocaleString()} by ${actor}`);
        if (onVaultUpdate) onVaultUpdate({ newVault, diff, actor });
        // Invalidate faction vault cache
        try { invalidateLeaderboard(); } catch {}
      }
      prevVaultRef.current = newVault;

      setRealtimeFaction({
        name: f?.faction_name,
        rank: data?.rank,
        vault: newVault,
        territories: f?.territory_count || 0,
        territoryBonus: 0,
        color: f?.banner_color || '#00D9FF',
        factionId: f?.id,
      });
      markUpdated();
    }
  }, [userId, markUpdated, showVaultToast, onVaultUpdate]);

  // Fetch net worth snapshots for 7-day trend
  const refreshNetWorthTrend = useCallback(async (currentNetWorth) => {
    if (!userId) return;
    try {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString();
      const { data, error } = await supabase
        ?.from('net_worth_snapshots')
        ?.select('net_worth, snapshot_date, created_at')
        ?.eq('user_id', userId)
        ?.gte('created_at', sevenDaysAgo)
        ?.order('created_at', { ascending: true })
        ?.limit(50);

      if (!error && mountedRef?.current) {
        const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        let trendPoints;

        if (data && data?.length >= 2) {
          // Use real snapshot data
          trendPoints = data?.map((snap, i) => ({
            label: snap?.snapshot_date
              ? new Date(snap?.snapshot_date)?.toLocaleDateString('en-US', { weekday: 'short' })
              : days?.[i % 7],
            value: parseFloat(snap?.net_worth || 0),
          }));

          // Calculate 7-day change
          const oldest = parseFloat(data?.[0]?.net_worth || 0);
          const newest = parseFloat(data?.[data?.length - 1]?.net_worth || currentNetWorth || 0);
          const change = newest - oldest;
          const changePercent = oldest > 0 ? (change / oldest) * 100 : 0;
          setNetWorthChange({ change, changePercent });
        } else {
          // Fallback: generate synthetic trend from current net worth
          const nw = currentNetWorth || 0;
          trendPoints = days?.map((label, i) => ({
            label,
            value: nw * (0.92 + i * 0.013),
          }));
          setNetWorthChange({ change: 0, changePercent: 0 });
        }

        setNetWorthTrend(trendPoints);
        markUpdated();
      }
    } catch (err) {
      console.warn('[Realtime] refreshNetWorthTrend error:', err);
    }
  }, [userId, markUpdated]);

  // Fetch recent audit_log activities
  const refreshActivities = useCallback(async (optimisticEntry) => {
    if (!userId) return;
    try {
      // Optimistic prepend before server fetch
      if (optimisticEntry && mountedRef?.current) {
        setRealtimeActivities(prev => {
          const current = prev || [];
          return [optimisticEntry, ...current]?.slice(0, MAX_ACTIVITIES);
        });
      }

      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.select('id, action_type, description, metadata, created_at')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(MAX_ACTIVITIES);

      if (!error && data && mountedRef?.current) {
        const mapped = data?.map(log => ({
          id: `audit-${log?.id}`,
          type: mapAuditType(log?.action_type),
          message: log?.description || formatAuditMessage(log?.action_type, log?.metadata),
          detail: formatAuditDetail(log?.action_type, log?.metadata),
          outcome: log?.metadata?.success === false ? 'failure' : 'success',
          time: new Date(log?.created_at)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          isNew: false,
        }));
        setRealtimeActivities(mapped);
        markUpdated();
      }
    } catch (err) {
      console.warn('[Realtime] refreshActivities error:', err);
    }
  }, [userId, markUpdated]);

  const cleanupChannels = useCallback(() => {
    channelsRef?.current?.forEach(ch => {
      try { supabase?.removeChannel(ch); } catch {}
    });
    channelsRef.current = [];
    connectedCountRef.current = 0;
  }, []);

  const updateOverallStatus = useCallback(() => {
    const connected = connectedCountRef?.current;
    const total = totalChannelsRef?.current;
    if (connected >= total && total > 0) {
      setConnectionStatus('connected');
    }
  }, []);

  const scheduleRetry = useCallback((setupFn) => {
    if (!mountedRef?.current) return;
    if (retryCountRef?.current >= MAX_RETRIES) {
      setConnectionStatus('offline');
      return;
    }
    setConnectionStatus('reconnecting');
    const delay = Math.min(BACKOFF_BASE * Math.pow(2, retryCountRef?.current), BACKOFF_MAX);
    retryCountRef.current += 1;
    clearTimeout(retryTimerRef?.current);
    retryTimerRef.current = setTimeout(() => {
      if (mountedRef?.current) setupFn();
    }, delay);
  }, []);

  const setupSubscriptions = useCallback(() => {
    if (!userId || !mountedRef?.current) return;

    cleanupChannels();
    setConnectionStatus('connecting');
    connectedCountRef.current = 0;

    const channels = [];
    let expectedChannels = 0;

    const onSubscribed = (channelName) => {
      if (!mountedRef?.current) return;
      connectedCountRef.current += 1;
      retryCountRef.current = 0;
      updateHealth(channelName, 'connected');
      updateOverallStatus();
    };

    const onError = (channelName, status) => {
      if (!mountedRef?.current) return;
      updateHealth(channelName, 'error');
      setConnectionStatus('reconnecting');
      scheduleRetry(setupSubscriptions);
    };

    // ── 1. player_stats subscription ──────────────────────────────────────
    expectedChannels++;
    const statsCh = supabase
      ?.channel(`dashboard-stats-${userId}`)
      ?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'player_stats', filter: `user_id=eq.${userId}` },
        (payload) => {
          if (!mountedRef?.current) return;
          const newStats = payload?.new;
          if (!newStats) return;

          // Level-up detection
          if (prevLevelRef?.current !== null && newStats?.level > prevLevelRef?.current) {
            setLevelUpNotif({ level: newStats?.level, timestamp: Date.now() });
            setTimeout(() => mountedRef?.current && setLevelUpNotif(null), 5000);
          }
          prevLevelRef.current = newStats?.level;

          setRealtimePlayerStats(newStats);
          markUpdated();

          // Refresh net worth trend when player_stats updates (net worth may have changed)
          refreshNetWorthTrend(parseFloat(newStats?.net_worth || 0));
          // Invalidate leaderboard cache
          try { invalidateLeaderboard(); } catch {}
        }
      )
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') onSubscribed('player_stats');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') onError('player_stats', status);
      });
    channels?.push(statsCh);

    // ── 2. net_worth_snapshots subscription ───────────────────────────────
    expectedChannels++;
    const netWorthCh = supabase
      ?.channel(`dashboard-networth-${userId}`)
      ?.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'net_worth_snapshots', filter: `user_id=eq.${userId}` },
        (payload) => {
          if (!mountedRef?.current) return;
          const snap = payload?.new;
          if (!snap) return;

          // Prepend new snapshot to trend
          setNetWorthTrend(prev => {
            const newPoint = {
              label: new Date(snap?.created_at || Date.now())?.toLocaleDateString('en-US', { weekday: 'short' }),
              value: parseFloat(snap?.net_worth || 0),
            };
            const updated = [...(prev || []), newPoint]?.slice(-7);
            // Recalculate change
            if (updated?.length >= 2) {
              const oldest = updated?.[0]?.value;
              const newest = updated?.[updated?.length - 1]?.value;
              const change = newest - oldest;
              const changePercent = oldest > 0 ? (change / oldest) * 100 : 0;
              setNetWorthChange({ change, changePercent });
            }
            return updated;
          });
          markUpdated();
        }
      )
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') onSubscribed('net_worth_snapshots');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') onError('net_worth_snapshots', status);
      });
    channels?.push(netWorthCh);

    // ── 3. portfolio_holdings subscription ────────────────────────────────
    expectedChannels++;
    const portfolioCh = supabase
      ?.channel(`dashboard-portfolio-${userId}`)
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'portfolio_holdings', filter: `user_id=eq.${userId}` },
        () => { refreshHoldings(); }
      )
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') onSubscribed('portfolio_holdings');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') onError('portfolio_holdings', status);
      });
    channels?.push(portfolioCh);

    // ── 4. factions vault subscription ────────────────────────────────────
    expectedChannels++;
    const factionVaultCh = supabase
      ?.channel(`dashboard-faction-vault-${userId}`)
      ?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'factions' },
        (payload) => { refreshFaction(payload); }
      )
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') onSubscribed('factions');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') onError('factions', status);
      });
    channels?.push(factionVaultCh);

    // ── 5. faction_members subscription ───────────────────────────────────
    expectedChannels++;
    const factionMemberCh = supabase
      ?.channel(`dashboard-faction-member-${userId}`)
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'faction_members', filter: `user_id=eq.${userId}` },
        () => { refreshFaction({}); }
      )
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') onSubscribed('faction_members');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') onError('faction_members', status);
      });
    channels?.push(factionMemberCh);

    // ── 6. audit_logs subscription (activity feed) ────────────────────────
    expectedChannels++;
    const auditCh = supabase
      ?.channel(`dashboard-audit-${userId}`)
      ?.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'audit_logs', filter: `user_id=eq.${userId}` },
        (payload) => {
          if (!mountedRef?.current) return;
          const log = payload?.new;
          if (!log) return;

          // Immediately prepend new activity with slide-in animation flag
          const newActivity = {
            id: `audit-${log?.id || Date.now()}`,
            type: mapAuditType(log?.action_type),
            message: log?.description || formatAuditMessage(log?.action_type, log?.metadata),
            detail: formatAuditDetail(log?.action_type, log?.metadata),
            outcome: log?.metadata?.success === false ? 'failure' : 'success',
            time: new Date(log?.created_at || Date.now())?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            isNew: true,
          };

          setRealtimeActivities(prev => {
            const current = prev || [];
            // Remove optimistic duplicate if present
            const filtered = current?.filter(a => a?.id !== newActivity?.id);
            return [newActivity, ...filtered]?.slice(0, MAX_ACTIVITIES);
          });
          markUpdated();

          // Clear isNew flag after animation
          setTimeout(() => {
            if (mountedRef?.current) {
              setRealtimeActivities(prev =>
                prev?.map(a => a?.id === newActivity?.id ? { ...a, isNew: false } : a)
              );
            }
          }, 1000);
        }
      )
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') onSubscribed('audit_logs');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') onError('audit_logs', status);
      });
    channels?.push(auditCh);

    totalChannelsRef.current = expectedChannels;
    channelsRef.current = channels?.filter(Boolean);
  }, [userId, cleanupChannels, refreshHoldings, refreshFaction, refreshNetWorthTrend, markUpdated, updateHealth, updateOverallStatus, scheduleRetry]);

  useEffect(() => {
    mountedRef.current = true;
    if (userId) {
      // Initialize prevLevel from initial stats
      prevLevelRef.current = initialPlayerStats?.level ?? null;
      prevVaultRef.current = initialFaction?.vault ?? null;
      setupSubscriptions();
      // Initial data fetch
      refreshNetWorthTrend(parseFloat(initialPlayerStats?.net_worth || 0));
      refreshActivities();
    }
    return () => {
      mountedRef.current = false;
      clearTimeout(retryTimerRef?.current);
      cleanupChannels();
    };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent of level up
  useEffect(() => {
    if (levelUpNotif && onLevelUp) onLevelUp(levelUpNotif?.level);
  }, [levelUpNotif]); // eslint-disable-line react-hooks/exhaustive-deps

  // Optimistic activity add (called externally for user's own actions)
  const addOptimisticActivity = useCallback((activity) => {
    if (!mountedRef?.current) return;
    const entry = {
      id: `optimistic-${Date.now()}`,
      isNew: true,
      ...activity,
    };
    setRealtimeActivities(prev => {
      const current = prev || [];
      return [entry, ...current]?.slice(0, MAX_ACTIVITIES);
    });
  }, []);

  // Manual refresh
  const manualRefresh = useCallback(async () => {
    if (!userId) return;
    await Promise.all([
      refreshHoldings(),
      refreshFaction({}),
      refreshActivities(),
      refreshNetWorthTrend(parseFloat(realtimePlayerStats?.net_worth || initialPlayerStats?.net_worth || 0)),
    ]);
  }, [userId, refreshHoldings, refreshFaction, refreshActivities, refreshNetWorthTrend, realtimePlayerStats, initialPlayerStats]);

  // Merge realtime data with initial data (realtime takes precedence)
  const playerStats = realtimePlayerStats ?? initialPlayerStats;
  const holdings = realtimeHoldings ?? initialHoldings;
  const faction = realtimeFaction ?? initialFaction;

  return {
    playerStats,
    holdings,
    faction,
    realtimeActivities,
    netWorthTrend,
    netWorthChange,
    connectionStatus,
    lastUpdated,
    levelUpNotif,
    vaultToast,
    subscriptionHealth,
    addOptimisticActivity,
    manualRefresh,
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────

function mapAuditType(actionType) {
  if (!actionType) return 'system';
  const t = actionType?.toLowerCase();
  if (t?.includes('trade') || t?.includes('buy') || t?.includes('sell') || t?.includes('stock')) return 'trade';
  if (t?.includes('crime') || t?.includes('heist') || t?.includes('theft')) return 'crime';
  if (t?.includes('faction') || t?.includes('vault') || t?.includes('war')) return 'faction';
  if (t?.includes('business') || t?.includes('revenue') || t?.includes('purchase')) return 'business';
  return 'system';
}

function formatAuditMessage(actionType, metadata) {
  if (!actionType) return 'System event';
  const t = actionType?.toLowerCase();
  if (t?.includes('buy')) return `Bought ${metadata?.quantity || ''} ${metadata?.ticker || 'shares'}`;
  if (t?.includes('sell')) return `Sold ${metadata?.quantity || ''} ${metadata?.ticker || 'shares'}`;
  if (t?.includes('trade')) return `Trade executed: ${metadata?.ticker || 'stock'}`;
  if (t?.includes('crime')) return `${metadata?.crime_name || 'Crime attempt'}`;
  if (t?.includes('business')) return `Business: ${metadata?.business_name || actionType}`;
  if (t?.includes('faction')) return `Faction action: ${metadata?.action || actionType}`;
  return actionType?.replace(/_/g, ' ');
}

function formatAuditDetail(actionType, metadata) {
  if (!metadata) return null;
  const t = (actionType || '')?.toLowerCase();
  if (t?.includes('buy') || t?.includes('sell') || t?.includes('trade')) {
    const price = metadata?.price ? `@ $${parseFloat(metadata?.price)?.toFixed(2)}` : '';
    const pnl = metadata?.pnl ? ` · PnL: $${parseFloat(metadata?.pnl)?.toFixed(2)}` : '';
    return price + pnl || null;
  }
  if (t?.includes('crime')) {
    return metadata?.success === false
      ? 'FAILED'
      : metadata?.reward ? `SUCCESS — +$${parseFloat(metadata?.reward)?.toLocaleString()}` : 'SUCCESS';
  }
  if (t?.includes('business')) {
    return metadata?.revenue ? `Revenue: +$${parseFloat(metadata?.revenue)?.toLocaleString()}` : null;
  }
  return null;
}

export default useDashboardRealtime;
